import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Dumbbell, Clock, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Exercise {
  id: string;
  name: string;
  sets: number | null;
  reps: number | null;
  weight_kg: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  notes: string | null;
  muscle_groups: string[];
  equipment: string | null;
  created_at?: string;
  set_details?: any;
}

interface Workout {
  id: string;
  title: string;
  description: string | null;
  workout_type: string | null;
  duration_minutes: number | null;
  calories_burned: number | null;
  completed_at: string;
  notes: string | null;
  exercises: Exercise[];
}

interface WorkoutImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkoutSelect: (formattedWorkout: string) => void;
}

export const WorkoutImportModal = ({ isOpen, onClose, onWorkoutSelect }: WorkoutImportModalProps) => {
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadWorkouts();
    }
  }, [isOpen, user]);

  const loadWorkouts = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          *,
          exercises(*)
        `)
        .eq('user_id', user.id)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setWorkouts(data || []);
    } catch (error) {
      console.error('Error loading workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatWorkout = (workout: Workout): string => {
    if (!workout.completed_at) return '';
    
    const date = new Date(workout.completed_at).toLocaleDateString();
    const time = new Date(workout.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    let formatted = `**${workout.title}**\n`;
    formatted += `${date} at ${time}\n`;
    if (workout.duration_minutes) {
      formatted += `Duration: ${workout.duration_minutes} minutes\n`;
    }
    if (workout.workout_type) {
      formatted += `Type: ${workout.workout_type}\n`;
    }
    if (workout.notes) {
      formatted += `Notes: ${workout.notes}\n`;
    }
    
    formatted += `\n**Detailed Exercises:**\n`;
    
    // Special formatting for running workouts - summarized metrics only (exclude raw GPS)
    if (workout.workout_type === 'running') {
      const runningExercise = workout.exercises?.find(e => e.name === 'Running');
      if (runningExercise) {
        formatted += `**Running Session**\n`;
        if (runningExercise.distance_km) {
          formatted += `   • Distance: ${runningExercise.distance_km} km\n`;
        }
        if (runningExercise.duration_seconds) {
          const mins = Math.floor(runningExercise.duration_seconds / 60);
          const secs = runningExercise.duration_seconds % 60;
          formatted += `   • Duration: ${mins}:${String(secs).padStart(2, '0')}\n`;
        }
        if (runningExercise.notes) {
          try {
            const runData = JSON.parse(runningExercise.notes);
            if (runData.pace) formatted += `   • Avg Pace: ${runData.pace}/km\n`;
            if (runData.avgSpeedKmh) formatted += `   • Avg Speed: ${runData.avgSpeedKmh} km/h\n`;
            if (runData.feeling) formatted += `   • Feeling: ${runData.feeling}\n`;
            if (runData.splits && Array.isArray(runData.splits) && runData.splits.length > 0) {
              formatted += `   • Splits: ${runData.splits.map((s: any) => `${s.km}km @ ${s.pace}/km`).join(', ')}\n`;
            }
            // Explicitly exclude routePoints - raw GPS would make message unreadable
          } catch {
            formatted += `   • Details: ${runningExercise.notes.substring(0, 200)}${runningExercise.notes.length > 200 ? '...' : ''}\n`;
          }
        }
        if (workout.calories_burned) {
          formatted += `   • Calories: ~${workout.calories_burned} kcal\n`;
        }
      }
    } else {
      // Strength workouts: normalize legacy rows (one row per set) and new format (set_details)
      const byName = new Map<string, Exercise[]>();
      for (const ex of workout.exercises || []) {
        const key = (ex.name || '').trim();
        if (!key) continue;
        byName.set(key, [...(byName.get(key) || []), ex]);
      }

      const grouped = Array.from(byName.entries());

      grouped.forEach(([exerciseName, rows], index) => {
        formatted += `${index + 1}. **${exerciseName}**\n`;

        // Prefer the row that contains set_details (new format). If multiple, take the longest.
        const bestWithDetails = rows
          .filter(r => Array.isArray((r as any).set_details) && ((r as any).set_details?.length || 0) > 0)
          .sort((a, b) => (((b as any).set_details?.length || 0) - ((a as any).set_details?.length || 0)))[0];

        type SetLine = { set_number: number; reps: number | null; weight_kg: number | null };
        let sets: SetLine[] = [];

        if (bestWithDetails) {
          const raw = ((bestWithDetails as any).set_details as any[])
            .filter(s => s && (s.completed ?? true));

          sets = raw
            .map((s, idx) => ({
              set_number: typeof s.set_number === 'number' ? s.set_number : idx + 1,
              reps: typeof s.reps === 'number' ? s.reps : null,
              weight_kg: typeof (s.weight_kg ?? s.weight) === 'number' ? (s.weight_kg ?? s.weight) : null,
            }))
            .sort((a, b) => a.set_number - b.set_number);
        } else {
          // Legacy:
          // - WorkoutLogger style: 1 row per exercise with `sets` as total sets.
          // - Older GymWorkout style: multiple rows per exercise, each row = one set (often `sets` is the set number).
          if (rows.length === 1) {
            const r = rows[0];
            if (
              typeof r.sets === "number" &&
              r.sets > 1 &&
              typeof r.reps === "number" &&
              typeof r.weight_kg === "number"
            ) {
              sets = Array.from({ length: r.sets }, (_, i) => ({
                set_number: i + 1,
                reps: r.reps,
                weight_kg: r.weight_kg,
              }));
            } else {
              sets = [
                {
                  set_number: 1,
                  reps: typeof r.reps === "number" ? r.reps : null,
                  weight_kg: typeof r.weight_kg === "number" ? r.weight_kg : null,
                },
              ];
            }
          } else {
            sets = rows
              .map((r, idx) => ({
                set_number: typeof r.sets === "number" && r.sets > 0 ? r.sets : idx + 1,
                reps: typeof r.reps === "number" ? r.reps : null,
                weight_kg: typeof r.weight_kg === "number" ? r.weight_kg : null,
              }))
              .sort((a, b) => a.set_number - b.set_number);
          }
        }

        if (sets.length > 0) {
          const first = sets[0];
          const allSame =
            first.reps !== null &&
            first.weight_kg !== null &&
            sets.every(s => s.reps === first.reps && s.weight_kg === first.weight_kg);

          if (allSame) {
            formatted += `   • ${sets.length} sets × ${first.reps} reps @ ${first.weight_kg}kg\n`;
          } else {
            formatted += `   • ${sets.length} sets\n`;
          }

          sets.forEach((s, i) => {
            const repsText = s.reps !== null ? `${s.reps} reps` : `? reps`;
            const weightText = s.weight_kg !== null ? `${s.weight_kg}kg` : `?kg`;
            formatted += `     - Set ${i + 1}: ${repsText} × ${weightText}\n`;
          });
        } else {
          formatted += `   • No completed sets\n`;
        }

        const meta = bestWithDetails || rows[0];
        if (meta?.muscle_groups && meta.muscle_groups.length > 0) {
          formatted += `   • Muscle groups: ${meta.muscle_groups.join(', ')}\n`;
        }
        if (meta?.equipment) {
          formatted += `   • Equipment: ${meta.equipment}\n`;
        }
        if (meta?.notes) {
          formatted += `   • Notes: ${meta.notes}\n`;
        }
        formatted += `\n`;
      });
    }

    return formatted;
  };

  const handleSelectWorkout = (workout: Workout) => {
    const formattedWorkout = formatWorkout(workout);
    onWorkoutSelect(formattedWorkout);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-primary" />
            Import Previous Workout
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading workouts...
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No previous workouts found
            </div>
          ) : (
            <div className="space-y-3">
              {workouts.map((workout) => (
                <Card 
                  key={workout.id} 
                  className="cursor-pointer hover:bg-accent/50 transition-colors border-border/50"
                  onClick={() => handleSelectWorkout(workout)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{workout.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(workout.completed_at).toLocaleDateString()}
                          </div>
                          {workout.duration_minutes && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {workout.duration_minutes}min
                            </div>
                          )}
                        </div>
                      </div>
                      {workout.workout_type && (
                        <Badge variant="secondary" className="text-xs">
                          {workout.workout_type}
                        </Badge>
                      )}
                    </div>

                    {workout.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {workout.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {workout.exercises.slice(0, 4).map((exercise, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs bg-background/50 px-2 py-1 rounded-md">
                          <Target className="h-3 w-3" />
                          {exercise.name}
                        </div>
                      ))}
                      {workout.exercises.length > 4 && (
                        <div className="text-xs text-muted-foreground px-2 py-1">
                          +{workout.exercises.length - 4} more
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};